import { Grid, Box } from '@chakra-ui/react'
import { FC, useMemo, useEffect } from 'react'
import { BiPackage } from 'react-icons/bi'
import { FaTruckLoading, FaWeight } from 'react-icons/fa'
import { MdOutlineRecommend } from 'react-icons/md'

import useMrpDistribution from '@hook/mrp/use-distribution'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Layout from '@component/Layout'
import InfoCard from '@component/Layout/Card/Info'
import CardSlider from '@component/Layout/CardSlider'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import TableGrid from '@component/Layout/TableGrid'
import GridQuantity from '@component/Layout/TableGrid/GridQuantity'
import LayoutTools from '@component/Layout/Tools'

import { disableActionsByDefault } from '@helper/mrp/disableActionsByDefault'
import { validateAppVersion } from '@helper/util/storangeClear'
import generic from '@service/generic'

const MrpDistribution: FC = () => {
  const { menuToggle } = useLayout()
  const { getFilters } = useFilters()

  const {
    loading,
    fetching,
    headers,
    actions,
    tableInteraction,
    calc,
    data,
    filename,
    params,
    query,
    filters,
    setFilters,
    handleLocalParams,
    getSupplyCommands,
    setFilename,
    setQuery,
    getData,
    filtersList,
    handleUpdateParams,
    clearAllFilters,
    totalCount
  } = useMrpDistribution()

  useEffect(() => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        validateAppVersion(data.version)

        if (data.valor === 'true') {
          window.location.href = '/manutencao'
        } else {
          getFilters({
            companies: true,
            worlds: true,
            centers: true,
            providersForDistribution: true,
            categoryManagers: true,
            productManagers: true,
            distributionCenters: true,
            compactCategories: true,
            categories: true,
            providersForPurchase: true,
            abcCurves: true,
            gruposSeparacao: true,
            typeOfAlert: true,
            subCategories: true
          })
          handleLocalParams()
          getSupplyCommands()
        }
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { hasSessions } = useSessions()

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 142px)',
    [menuToggle, hasSessions]
  )

  const tableHeight = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 272px)'
          : 'calc(100vh - 241px)'
        : hasSessions
        ? 'calc(100vh - 413px)'
        : 'calc(100vh - 382px)',
    [menuToggle, hasSessions]
  )

  return (
    <Layout title="MRP Distribuição">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!filters ? '100vw' : '260px calc(100vw - 260px)'}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            values={params}
            onChange={handleUpdateParams}
            loading={loading}
            canCancel={fetching}
            onFilter={getData}
            onClearAllFilters={clearAllFilters}
          />
        )}
        <Box>
          <LayoutTools
            title={filename}
            filters={filters}
            actions={actions}
            query={query}
            onSearchChange={setQuery}
            onTitleChange={setFilename}
            toggleFilters={setFilters.toggle}
            isDisabled={loading || fetching || totalCount === 0}
            disableActionsByDefault={disableActionsByDefault}
          />
          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros no banco de dados"
            />
          ) : totalCount === 0 ? (
            <LayoutEmpty
              title="Nada encontrado!"
              message="Filtre sua busca ao lado para obter informações"
            />
          ) : (
            <>
              <CardSlider isFilters={filters}>
                <InfoCard
                  value={calc.skusDistintos}
                  title="SKUs distintos"
                  icon={<BiPackage color="#1459B4" />}
                  decimals={1}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={calc.custoRecomendado}
                  title="Custo Recomendado"
                  icon={<MdOutlineRecommend color="#C22C31" />}
                  decimals={2}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={calc.pesoTotal}
                  title="Peso Total"
                  icon={<FaWeight color="#24B85F" />}
                  decimals={2}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={calc.custoTotalDaDistribuicao}
                  title="Custo Total da Distribuição"
                  icon={<FaTruckLoading color="#308AFF " />}
                  decimals={2}
                  isLoading={fetching}
                  noShadow
                />
              </CardSlider>

              <GridQuantity
                qtd={String(totalCount)}
                disabled={totalCount === 0}
              />
              <TableGrid
                id="distribution-table"
                headers={headers}
                query={query}
                data={data}
                loading={fetching}
                actions={tableInteraction}
                itsSortable={true}
                height={tableHeight}
                entity="mrp-distribution"
              />
            </>
          )}
        </Box>
      </Grid>
    </Layout>
  )
}

export default MrpDistribution
